import React, { useEffect, useId, useState, useRef } from "react";
import { Info } from "lucide-react";
import { useRegisterTopEscapeClose } from "../hooks/useEscapeCloseStack";
import { useLanguage } from "../contexts/LanguageContext";

function canUseFinePointerHover() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

/**
 * Field title with an explanation bubble.
 * Desktop (fine pointer + hover): show on hover.
 * Mobile / touch: toggle on click of the title.
 */
export default function FieldHelpLabel({ children, help }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [hoverCapable, setHoverCapable] = useState(canUseFinePointerHover);
  const rootRef = useRef(null);
  const tooltipId = useId();
  const labelText = typeof children === "string" ? children : "";

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const sync = () => setHoverCapable(mq.matches);
    sync();
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", sync);
      return () => mq.removeEventListener("change", sync);
    }
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);

  useEffect(() => {
    if (!open || hoverCapable) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, hoverCapable]);

  useRegisterTopEscapeClose(() => setOpen(false), open);

  if (!help) {
    return <div className="block text-sm font-medium mb-1">{children}</div>;
  }

  const show = () => setOpen(true);
  const hide = () => setOpen(false);

  return (
    <div ref={rootRef} className="relative mb-1">
      <button
        type="button"
        className="inline-flex items-center gap-1 max-w-full text-sm font-medium text-left text-gray-900 cursor-help rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        aria-label={`${labelText}. ${t("fieldHelp.moreInfo")}`}
        onClick={() => {
          if (!hoverCapable) setOpen((value) => !value);
        }}
        onMouseEnter={() => {
          if (hoverCapable) show();
        }}
        onMouseLeave={() => {
          if (hoverCapable) hide();
        }}
        onFocus={() => {
          if (hoverCapable) show();
        }}
        onBlur={() => {
          if (hoverCapable) hide();
        }}
      >
        <span>{children}</span>
        <Info className="w-3.5 h-3.5 text-gray-400 shrink-0" aria-hidden />
      </button>
      {open && (
        <div
          id={tooltipId}
          role="tooltip"
          className="absolute left-0 top-full z-30 pt-1.5 w-72 max-w-[calc(100vw-2.5rem)]"
        >
          <div className="relative rounded-lg bg-slate-800 px-3 py-2 text-xs leading-relaxed text-white shadow-lg">
            <span
              className="absolute -top-1 left-4 h-2 w-2 rotate-45 bg-slate-800"
              aria-hidden
            />
            {help}
          </div>
        </div>
      )}
    </div>
  );
}
